// Lysforhold — sol, måne, BMNT/EENT og NVG for operasjonsplanlegging.
//
// Operativt spørsmål panelet svarer på: «når kan vi bevege oss i mørke, og hvor
// mye månelys får vi?» Derfor er BMNT/EENT (nautisk tussmørke, −12°) det mest
// prominente i panelet — det er tallene som går inn i ordreverket — mens
// soloppgang/nedgang og månefase er støtteinformasjon.
//
// Regnemotoren er `@skade/terrain` (Meeus-basert, verifisert mot USNO-tabeller).
// Panelet REGNER INGENTING selv utover formatering og plassering på tidslinja;
// all astronomi kommer fra `sunDay`/`sunPosition`/`moonDay`/`moonPhase`/
// `nvgAssessment`. Det er med vilje: to sol-implementasjoner i samme app er én
// for mye, og denne er den presise.
//
// ÆRLIGHET (UX_DESIGNSYSTEM §10) — panelet SKAL si dette høyt, ikke skjule det:
//   · `nvgAssessment` regner KLAR HIMMEL når skydekke ikke er oppgitt. Det er
//     det optimistiske tilfellet. Panelet henter skydekke fra hubbens
//     /met/forecast når feltet finnes, og skriver ellers eksplisitt at
//     vurderingen er teoretisk. Se `tolkSkydekke` for hvorfor feltet i dag
//     mangler.
//   · Tersklene som IKKE inntreffer (lyse sommernetter uten astronomisk mørke,
//     midnattssol, mørketid) får en setning som forklarer hvorfor — aldri et
//     tomt felt eller et nakent «—».
//   · Alle tider er UTC (Z) som standard, med lokal tid (Europe/Oslo) som
//     valgbar visning. Horisont-fenomener har flere minutters FYSISK usikkerhet
//     (refraksjon over kald fjord), så sekunder vises ikke.
import '../lysforhold.css';
import {
  lightPhaseForElevation,
  moonDay,
  moonPhase,
  nextLightPhaseChange,
  nvgAssessment,
  sunDay,
  sunPosition,
  type LightPhase,
  type MoonDay,
  type MoonPhaseInfo,
  type NvgAssessment,
  type NvgLightLevel,
  type SunDay,
} from '@skade/terrain';

const DØGN_MS = 86_400_000;

// ── Hooks (samme mønster som ui/weather.ts) ──────────────────────────────────

export interface LysforholdHooks {
  /** Punktet lysforholdene regnes for (default: kartsenter). */
  center: () => { lon: number; lat: number };
  /** Overstyrbar «nå» — brukes av tester og kan kobles til tidsmaskinen. */
  now?: () => number;
  /** Overstyrbar skydekke-kilde (test / annen transport). */
  skydekke?: (lat: number, lon: number) => Promise<SkydekkeSvar>;
}

// ── Tid og formatering ──────────────────────────────────────────────────────

export type Tidssone = 'UTC' | 'Europe/Oslo';

export const TIDSSONE_MERKE: Record<Tidssone, string> = {
  UTC: 'Z',
  'Europe/Oslo': 'lok',
};

/**
 * Klokkeslett «TT:MM» i valgt sone. UTC formateres fra ISO-strengen (ingen
 * ICU-avhengighet, samme grep som `ui/weather.ts` bruker); lokal tid går via
 * Intl med eksplisitt tidssone, så visningen ikke avhenger av maskinens sone.
 * null ⇒ '—'; kalleren SKAL i tillegg vise hvorfor tiden mangler.
 */
export function formaterKlokke(ms: number | null, sone: Tidssone = 'UTC'): string {
  if (ms === null || !Number.isFinite(ms)) return '—';
  if (sone === 'UTC') return new Date(ms).toISOString().slice(11, 16);
  try {
    return new Intl.DateTimeFormat('nb-NO', {
      timeZone: sone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(ms));
  } catch {
    // Uten tidssonedata er UTC det ærlige svaret framfor en gjettet lokaltid.
    return new Date(ms).toISOString().slice(11, 16);
  }
}

/** Dato «ÅÅÅÅ-MM-DD» i UTC — samme døgn `sunDay` regner for. */
export function formaterDatoUtc(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Varighet i klartekst: «4 t 07 min», «43 min», «< 1 min». */
export function formaterVarighet(sekunder: number): string {
  const s = Math.max(0, Math.round(sekunder));
  const min = Math.floor(s / 60);
  if (min < 1) return '< 1 min';
  const t = Math.floor(min / 60);
  const m = min % 60;
  if (t === 0) return `${m} min`;
  return `${t} t ${String(m).padStart(2, '0')} min`;
}

// ── Lysfaser ────────────────────────────────────────────────────────────────

export interface LysfaseTekst {
  navn: string;
  /** Kortform til tidslinja og status-taggen. */
  kort: string;
  /** Hva fasen betyr operativt — solhøyde-intervallet er med med vilje. */
  forklaring: string;
}

export const LYSFASE: Record<LightPhase, LysfaseTekst> = {
  dag: {
    navn: 'Dag',
    kort: 'DAG',
    forklaring: 'Sola er over horisonten. Full sikt, ingen lysdisiplin-vindu.',
  },
  'borgerlig-tussmørke': {
    navn: 'Borgerlig tussmørke',
    kort: 'BORG',
    forklaring:
      'Sol −0,8° til −6°. Kart kan leses uten lys, horisonten er tydelig over sjø. '
      + 'Ikke mørkt nok for skjult forflytning.',
  },
  'nautisk-tussmørke': {
    navn: 'Nautisk tussmørke',
    kort: 'NAUT',
    forklaring:
      'Sol −6° til −12°. Horisonten er så vidt skillbar. BMNT/EENT er kantene '
      + 'av dette båndet — grensen ordreverket bruker for mørke.',
  },
  'astronomisk-tussmørke': {
    navn: 'Astronomisk tussmørke',
    kort: 'ASTR',
    forklaring:
      'Sol −12° til −18°. Mørkt for øyet, men himmelen har fortsatt målbart '
      + 'restlys fra sola.',
  },
  natt: {
    navn: 'Natt',
    kort: 'NATT',
    forklaring: 'Sol under −18°. Full nattmørke — bare måne, stjerner og kunstig lys.',
  },
};

// ── Tidslinje: bånd og merker gjennom soldøgnet ──────────────────────────────

/** Ett lysfase-bånd på tidslinja. `fra`/`til` er andel 0–1 av soldøgnet. */
export interface Lysbånd {
  fase: LightPhase;
  fra: number;
  til: number;
}

/**
 * Tidslinja dekker SOLDØGNET [solmidnatt, solmidnatt + 24 t] — ikke
 * kalenderdøgnet. Det er samme vindu `sunDay` bruker, og det er nødvendig for at
 * BMNT → soloppgang → solnedgang → EENT skal komme i kronologisk rekkefølge i
 * stedet for å bli delt på midnatt.
 */
export function tidslinjeStartMs(day: SunDay): number {
  return day.solarMidnightMs;
}

/**
 * Lysfase-båndene gjennom soldøgnet, i rekkefølge og uten hull.
 *
 * Fasen ved vindusstart er kjent uten ekstra regning: vinduet starter på
 * solmidnatt, altså døgnets LAVESTE solhøyde. Derfra endrer hver terskel-kryssing
 * fasen ett hakk. Tersklene som ikke inntreffer (midnattssol, mørketid, lyse
 * sommernetter) er rett og slett ikke med i lista — da blir det ett langt bånd,
 * som er det korrekte svaret.
 */
export function lysbånd(day: SunDay): Lysbånd[] {
  const start = tidslinjeStartMs(day);
  const slutt = start + DØGN_MS;
  const grenser: { atMs: number; fase: LightPhase }[] = [];
  const legg = (atMs: number | null, fase: LightPhase) => {
    if (atMs !== null && Number.isFinite(atMs)) grenser.push({ atMs, fase });
  };
  // Stigende sol: mørkest → lysest.
  legg(day.dawnAstronomicalMs, 'astronomisk-tussmørke');
  legg(day.bmntMs, 'nautisk-tussmørke');
  legg(day.dawnCivilMs, 'borgerlig-tussmørke');
  legg(day.sunriseMs, 'dag');
  // Synkende sol: lysest → mørkest.
  legg(day.sunsetMs, 'borgerlig-tussmørke');
  legg(day.duskCivilMs, 'nautisk-tussmørke');
  legg(day.eentMs, 'astronomisk-tussmørke');
  legg(day.duskAstronomicalMs, 'natt');
  grenser.sort((a, b) => a.atMs - b.atMs);

  let fase = lightPhaseForElevation(day.minElevationDeg);
  let t = start;
  const ut: Lysbånd[] = [];
  for (const g of grenser) {
    const at = Math.min(Math.max(g.atMs, start), slutt);
    if (at > t) ut.push({ fase, fra: (t - start) / DØGN_MS, til: (at - start) / DØGN_MS });
    fase = g.fase;
    t = at;
  }
  if (t < slutt) ut.push({ fase, fra: (t - start) / DØGN_MS, til: 1 });
  return ut.filter((b) => b.til > b.fra);
}

/** Ett tidsmerke på tidslinja. `andel` er 0–1 av soldøgnet. */
export interface Tidsmerke {
  id: 'bmnt' | 'soloppgang' | 'solmiddag' | 'solnedgang' | 'eent';
  navn: string;
  atMs: number;
  andel: number;
  /** true for BMNT/EENT — de skal være visuelt sterkest. */
  viktig: boolean;
}

/** Merkene som finnes i døgnet, i kronologisk rekkefølge. */
export function tidsmerker(day: SunDay): Tidsmerke[] {
  const start = tidslinjeStartMs(day);
  const kandidater: { id: Tidsmerke['id']; navn: string; atMs: number | null; viktig: boolean }[] = [
    { id: 'bmnt', navn: 'BMNT', atMs: day.bmntMs, viktig: true },
    { id: 'soloppgang', navn: 'Soloppgang', atMs: day.sunriseMs, viktig: false },
    { id: 'solmiddag', navn: 'Solmiddag', atMs: day.solarNoonMs, viktig: false },
    { id: 'solnedgang', navn: 'Solnedgang', atMs: day.sunsetMs, viktig: false },
    { id: 'eent', navn: 'EENT', atMs: day.eentMs, viktig: true },
  ];
  return kandidater
    .filter((k): k is typeof k & { atMs: number } => k.atMs !== null)
    .map((k) => ({ id: k.id, navn: k.navn, atMs: k.atMs, andel: (k.atMs - start) / DØGN_MS, viktig: k.viktig }))
    .filter((m) => m.andel >= 0 && m.andel <= 1)
    .sort((a, b) => a.atMs - b.atMs);
}

/** Andel 0–1 av soldøgnet for et tidspunkt, eller null hvis utenfor vinduet. */
export function andelIVindu(ms: number, day: SunDay): number | null {
  const a = (ms - tidslinjeStartMs(day)) / DØGN_MS;
  return a < 0 || a > 1 ? null : a;
}

// ── Tilstand: midnattssol, mørketid og tersklene som ikke inntreffer ─────────

/** Forklaring på overordnet døgn-tilstand. null ved 'normal'. */
export function tilstandForklaring(day: SunDay, sone: Tidssone = 'UTC'): string | null {
  const merke = TIDSSONE_MERKE[sone];
  if (day.state === 'midnattssol') {
    return (
      `Midnattssol: sola er over horisonten hele døgnet (laveste solhøyde `
      + `${day.minElevationDeg.toFixed(1)}°). Ingen soloppgang, solnedgang eller tussmørke — `
      + 'regn med dagslys 24 t og ingen mørke-vindu.'
    );
  }
  if (day.state === 'mørketid') {
    let s =
      `Mørketid (polarnatt): sola kommer ikke over horisonten (høyeste solhøyde `
      + `${day.maxElevationDeg.toFixed(1)}°).`;
    if (day.dawnCivilMs !== null && day.duskCivilMs !== null) {
      s += ` Det er likevel borgerlig tussmørke midt på dagen, `
        + `${formaterKlokke(day.dawnCivilMs, sone)}–${formaterKlokke(day.duskCivilMs, sone)} ${merke} `
        + '— lyst nok til å lese kart uten lys.';
    } else if (day.bmntMs !== null && day.eentMs !== null) {
      s += ` Lysest midt på dagen er nautisk tussmørke, `
        + `${formaterKlokke(day.bmntMs, sone)}–${formaterKlokke(day.eentMs, sone)} ${merke}.`;
    } else {
      s += ' Ingen tussmørke i det hele tatt — mørkt hele døgnet.';
    }
    return s;
  }
  return null;
}

/**
 * Tersklene som IKKE inntreffer dette døgnet, med grunn. Dette er den vanligste
 * kilden til «tomme felt» i lysforhold-UI: i Oslo (59,9°N) kommer sola aldri
 * under −18° fra midten av april til slutten av august, så astronomisk mørke
 * finnes ikke — det er ikke en feil, og skal stå som en setning.
 */
export function terskelForklaringer(day: SunDay): string[] {
  const ut: string[] = [];
  const e = day.events;
  if (e.astronomical.state === 'alltid-over') {
    ut.push(
      'Astronomisk mørke (sol under −18°) inntreffer ikke dette døgnet: himmelen blir '
      + 'aldri helt mørk (lys sommernatt). Restlys fra sola er med hele natta.',
    );
  }
  if (e.nautical.state === 'alltid-over') {
    ut.push(
      'BMNT/EENT inntreffer ikke: sola kommer aldri under −12°, så det blir ikke '
      + 'nautisk mørke. Planlegg uten mørke-vindu.',
    );
  }
  if (e.civil.state === 'alltid-over') {
    ut.push('Borgerlig tussmørke inntreffer ikke: sola kommer aldri under −6°.');
  }
  if (e.civil.state === 'alltid-under' && day.state === 'mørketid') {
    ut.push('Borgerlig tussmørke inntreffer ikke: sola kommer aldri over −6°.');
  }
  if (e.nautical.state === 'alltid-under' && day.state === 'mørketid') {
    ut.push(
      'BMNT/EENT inntreffer ikke: sola kommer aldri over −12°. Hele døgnet er '
      + 'nautisk mørke eller mørkere.',
    );
  }
  return ut;
}

// ── NVG-nivå ────────────────────────────────────────────────────────────────

export interface NvgNivåTekst {
  navn: string;
  forklaring: string;
  /** 1–5, til trinn-indikatoren. */
  trinn: number;
}

export const NVG_NIVÅ: Record<NvgLightLevel, NvgNivåTekst> = {
  ingen: {
    navn: 'Ingen',
    forklaring:
      'Under NVG-ens ytelsesgrense (≈0,3 mlx — overskyet, månefri natt). '
      + 'Regn med IR-lyskaster eller termisk i stedet.',
    trinn: 1,
  },
  marginal: {
    navn: 'Marginal',
    forklaring:
      'Stjernelys alene (≈0,8 mlx). NVG virker, men kort rekkevidde, mye støy '
      + 'og dårlig dybdesyn.',
    trinn: 2,
  },
  brukbar: {
    navn: 'Brukbar',
    forklaring: 'Nok lys til forflytning og grov gjenkjenning (≈5–20 mlx).',
    trinn: 3,
  },
  god: {
    navn: 'God',
    forklaring: 'Måne middels høyt (≈20–80 mlx): god rekkevidde og kontrast.',
    trinn: 4,
  },
  utmerket: {
    navn: 'Utmerket',
    forklaring:
      'Full måne høyt på klar himmel (> 80 mlx). Merk baksiden: EGNE styrker er '
      + 'også godt synlige — for motparten med NVG også.',
    trinn: 5,
  },
};

/**
 * Forbeholdene som SKAL vises sammen med NVG-vurderingen: motorens egne notater
 * (de er allerede på norsk og eksplisitte om hva modellen ikke dekker) pluss
 * status på skydekke-koblingen.
 */
export function nvgForbehold(a: NvgAssessment, sky: SkydekkeSvar): string[] {
  return [...a.notes, skydekkeTekst(sky)];
}

// ── Månefase-symbol (SVG, ikke emoji — UX_DESIGNSYSTEM §6) ───────────────────

/**
 * Terminatorens halvakse som andel av måneradien, og buens retning.
 *
 * Terminatoren (skyggegrensa) projiseres som en ELLIPSE: halvakse
 * |1 − 2k| der k er belyst andel. k = 0,5 gir en rett linje (halvmåne),
 * k → 0/1 gir en halvsirkel. `sweep` velger om buen bukter mot den belyste
 * eller ubelyste sida, altså sigd (k < 0,5) kontra nesten-full (k > 0,5).
 */
export function månefaseTerminator(illuminertAndel: number): { rx: number; sweep: 0 | 1 } {
  const k = Math.min(1, Math.max(0, illuminertAndel));
  return { rx: Math.abs(1 - 2 * k), sweep: k < 0.5 ? 0 : 1 };
}

/**
 * Månefase som liten SVG-skive: ring for hele skiva, fylt flate for den belyste
 * delen. Arver farge via `currentColor`, så CSS-tokenene styrer utseendet.
 * Tiltakende måne er belyst på HØYRE side (nordlige halvkule); avtakende speiles.
 */
export function månefaseSvg(illuminertAndel: number, tiltakende: boolean, r = 8): string {
  const { rx, sweep } = månefaseTerminator(illuminertAndel);
  const rxAbs = (rx * r).toFixed(2);
  const bane =
    `M 0 ${-r} A ${r} ${r} 0 0 1 0 ${r} A ${rxAbs} ${r} 0 0 ${sweep} 0 ${-r}`;
  const g = tiltakende
    ? `<path d="${bane}" fill="currentColor"/>`
    : `<g transform="scale(-1,1)"><path d="${bane}" fill="currentColor"/></g>`;
  const boks = r + 1.5;
  return (
    `<svg viewBox="${-boks} ${-boks} ${boks * 2} ${boks * 2}" width="${boks * 2}" height="${boks * 2}" aria-hidden="true">`
    + `<circle r="${r}" fill="none" stroke="currentColor" stroke-width="1" opacity="0.35"/>`
    + g
    + '</svg>'
  );
}

// ── Skydekke fra MET (gjør NVG-vurderingen reell i stedet for teoretisk) ─────

export type SkydekkeKilde =
  | 'met'
  | 'mangler-felt'
  | 'rute-mangler'
  | 'krever-innlogging'
  | 'utilgjengelig';

export interface SkydekkeSvar {
  /** Skydekke 0–1, eller null når det ikke finnes. */
  fraksjon: number | null;
  kilde: SkydekkeKilde;
  /** Ærlig forklaring — vises ordrett i UI. */
  detalj: string;
}

/**
 * Tolk hubbens /met/forecast-svar til skydekke.
 *
 * STATUS I DAG (verifisert 2026-07): svaret inneholder IKKE skydekke. Hubben
 * proxyer api.met.no gjennom `parseLocationforecast` i `@skade/contracts`, og den
 * parseren plukker ut temperatur/vind/fukt/trykk/nedbør — men ikke
 * `cloud_area_fraction`. Funksjonen her leser feltet hvis det finnes (både
 * `cloudAreaFraction` og rå `cloud_area_fraction`), slik at NVG-vurderingen blir
 * reell i det sekundet parseren utvides, og sier tydelig fra så lenge det
 * mangler. MET oppgir skydekke i PROSENT; vi normaliserer til 0–1.
 */
export function tolkSkydekke(body: unknown): SkydekkeSvar {
  const serie = (body as { series?: unknown[] } | null)?.series;
  const første = Array.isArray(serie) ? serie[0] : undefined;
  const rå = første as Record<string, unknown> | undefined;
  const verdi = rå
    ? [rå.cloudAreaFraction, rå.cloud_area_fraction, rå.skydekke].find(
        (v) => typeof v === 'number' && Number.isFinite(v),
      )
    : undefined;
  if (typeof verdi === 'number') {
    // Tåler både 0–1 og 0–100 (MET bruker prosent).
    const f = verdi > 1 ? verdi / 100 : verdi;
    return {
      fraksjon: Math.min(1, Math.max(0, f)),
      kilde: 'met',
      detalj: `Skydekke ${Math.round(Math.min(1, Math.max(0, f)) * 100)} % fra MET (api.met.no) — regnet inn i NVG-vurderingen.`,
    };
  }
  return {
    fraksjon: null,
    kilde: 'mangler-felt',
    detalj:
      'Skydekke mangler i MET-svaret: hubbens /met/forecast går gjennom '
      + '`parseLocationforecast` (@skade/contracts), som ikke plukker ut '
      + '`cloud_area_fraction`. NVG-vurderingen under er derfor regnet på KLAR '
      + 'HIMMEL — det OPTIMISTISKE tilfellet. Ikke bruk den som skydekke-fasit i en ordre.',
  };
}

/** Ferdig UI-tekst for skydekke-status. */
export function skydekkeTekst(s: SkydekkeSvar): string {
  return s.detalj;
}

/**
 * Hent skydekke for et punkt via hubbens MET-proxy. Feiler PENT: værruten på
 * piloten kan svare 404 når Caddy-konfigen mangler `handle /met/*` (kjent
 * driftsfeil, jf. `ui/weather.ts` og deploy/route-smoke.sh) — da skal NVG-
 * vurderingen fortsatt vises, bare merket som klar-himmel-antakelse.
 */
export async function hentSkydekkeFraMet(
  lat: number,
  lon: number,
  fetchImpl?: typeof fetch,
): Promise<SkydekkeSvar> {
  const doFetch = fetchImpl ?? (typeof fetch === 'function' ? fetch.bind(globalThis) : undefined);
  if (!doFetch) {
    return { fraksjon: null, kilde: 'utilgjengelig', detalj: SKY_UTEN_NETT };
  }
  try {
    const r = await doFetch(`/met/forecast?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}`, {
      credentials: 'include',
    });
    if (r.status === 401) {
      return {
        fraksjon: null,
        kilde: 'krever-innlogging',
        detalj:
          'Skydekke krever innlogget tilkobling til SKADE-hub. NVG-vurderingen '
          + 'er regnet på KLAR HIMMEL (optimistisk).',
      };
    }
    if (r.status === 404) {
      return {
        fraksjon: null,
        kilde: 'rute-mangler',
        detalj:
          'Vær-ruten nås ikke (404) — hubbens /met-rute er ikke aktiv (kjør '
          + 'deploy/route-smoke.sh). Uten skydekke er NVG-vurderingen regnet på '
          + 'KLAR HIMMEL (optimistisk).',
      };
    }
    if (!r.ok) {
      return {
        fraksjon: null,
        kilde: 'utilgjengelig',
        detalj: `Vær utilgjengelig (${r.status}) — NVG-vurderingen er regnet på KLAR HIMMEL (optimistisk).`,
      };
    }
    return tolkSkydekke(await r.json());
  } catch {
    return { fraksjon: null, kilde: 'utilgjengelig', detalj: SKY_UTEN_NETT };
  }
}

const SKY_UTEN_NETT =
  'Ingen tilkobling til værkilden — NVG-vurderingen er regnet på KLAR HIMMEL '
  + '(det optimistiske tilfellet).';

// ── Samlet avlesning (ren, testbar) ─────────────────────────────────────────

export interface Lysavlesning {
  tidMs: number;
  lat: number;
  lon: number;
  day: SunDay;
  fase: LightPhase;
  /** Solhøyde og asimut nå. */
  solElevasjonDeg: number;
  solAsimutDeg: number;
  /** Tid til neste fase-overgang, null ved midnattssol/mørketid uten overgang. */
  nesteOvergang: { atMs: number; sekunder: number; til: LightPhase } | null;
  måne: MoonDay;
  månefase: MoonPhaseInfo;
  nvg: NvgAssessment;
  skydekke: SkydekkeSvar;
}

/** Regn ut hele avlesningen for et punkt og et tidspunkt. Ingen I/O. */
export function lysavlesning(
  tidMs: number,
  lat: number,
  lon: number,
  sky: SkydekkeSvar,
): Lysavlesning {
  const day = sunDay(tidMs, lat, lon);
  const sol = sunPosition(tidMs, lat, lon);
  const neste = nextLightPhaseChange(tidMs, lat, lon);
  return {
    tidMs,
    lat,
    lon,
    day,
    fase: lightPhaseForElevation(sol.elevationDeg),
    solElevasjonDeg: sol.elevationDeg,
    solAsimutDeg: sol.azimuthDeg,
    nesteOvergang: neste
      ? { atMs: neste.atMs, sekunder: neste.secondsUntil, til: neste.to }
      : null,
    måne: moonDay(tidMs, lat, lon),
    månefase: moonPhase(tidMs),
    nvg: nvgAssessment(tidMs, lat, lon, {
      ...(sky.fraksjon !== null ? { cloudCoverFraction: sky.fraksjon } : {}),
    }),
    skydekke: sky,
  };
}

/** Måne-opp/ned som lesbar linje, inkludert de normale «bare ett av dem»-døgnene. */
export function måneOppNedTekst(m: MoonDay, sone: Tidssone = 'UTC'): string {
  const merke = TIDSSONE_MERKE[sone];
  if (m.state === 'alltid-over') return 'Over horisonten hele døgnet (ingen nedgang)';
  if (m.state === 'alltid-under') return 'Under horisonten hele døgnet — ingen månelys';
  const opp = m.moonriseMs !== null ? `opp ${formaterKlokke(m.moonriseMs, sone)}` : 'ingen oppgang i døgnet';
  const ned = m.moonsetMs !== null ? `ned ${formaterKlokke(m.moonsetMs, sone)}` : 'ingen nedgang i døgnet';
  return `${opp} · ${ned} ${merke}`;
}

// ── Panel (DOM) ─────────────────────────────────────────────────────────────

/** Monokromt linjeikon til verktøylinja/kommandopaletten (sol over horisont). */
export const LYSFORHOLD_IKON =
  '<svg viewBox="0 0 20 20" width="18" height="18" stroke="currentColor" fill="none" '
  + 'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
  + '<path d="M2.5 14.5h15"/><circle cx="10" cy="11" r="3.2"/>'
  + '<path d="M10 3.5v2M4.8 5.8l1.4 1.4M15.2 5.8l-1.4 1.4M2.5 11h1.6M15.9 11h1.6"/></svg>';

export class LysforholdPanel {
  readonly el: HTMLElement;
  private open = false;
  private sone: Tidssone = 'UTC';
  private sub!: HTMLElement;
  private body!: HTMLElement;
  private soneBtn!: HTMLButtonElement;
  private timer: ReturnType<typeof setInterval> | null = null;
  private sisteSky: SkydekkeSvar = {
    fraksjon: null,
    kilde: 'utilgjengelig',
    detalj: 'Skydekke ikke hentet ennå — NVG-vurderingen er regnet på KLAR HIMMEL (optimistisk).',
  };
  private sistePunkt: { lat: number; lon: number } | null = null;

  constructor(private hooks: LysforholdHooks) {
    this.el = this.bygg();
  }

  private nå(): number {
    return this.hooks.now ? this.hooks.now() : Date.now();
  }

  toggle() {
    this.open = !this.open;
    this.el.classList.toggle('open', this.open);
    if (this.open) {
      this.lastForSenter();
      // Fasen og «tid til neste overgang» er ferskvare — oppdatér hvert minutt
      // så panelet ikke lyver om nåtilstanden mens det står åpent.
      this.timer ??= setInterval(() => this.tegn(), 60_000);
    } else if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /** Hent for kartsenter (samme mønster som WeatherPanel). */
  lastForSenter() {
    const c = this.hooks.center();
    this.visForPunkt(c.lat, c.lon);
  }

  /** Vis lysforhold for et valgt punkt — offentlig så kartklikk kan drive det. */
  visForPunkt(lat: number, lon: number) {
    this.sistePunkt = { lat, lon };
    this.tegn();
    void this.hentSky(lat, lon);
  }

  private async hentSky(lat: number, lon: number) {
    const hent = this.hooks.skydekke ?? ((a: number, o: number) => hentSkydekkeFraMet(a, o));
    const svar = await hent(lat, lon);
    // Punktet kan ha endret seg mens vi ventet — da er svaret uinteressant.
    if (this.sistePunkt && this.sistePunkt.lat === lat && this.sistePunkt.lon === lon) {
      this.sisteSky = svar;
      this.tegn();
    }
  }

  private bygg(): HTMLElement {
    const p = div('report-composer lys-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Lysforhold — sol og måne'));

    this.soneBtn = document.createElement('button');
    this.soneBtn.type = 'button';
    this.soneBtn.className = 'lys-sone';
    this.soneBtn.onclick = () => {
      this.sone = this.sone === 'UTC' ? 'Europe/Oslo' : 'UTC';
      this.oppdaterSoneBtn();
      this.tegn();
    };
    this.oppdaterSoneBtn();

    const refresh = document.createElement('button');
    refresh.type = 'button';
    refresh.className = 'lys-refresh';
    refresh.title = 'Regn på nytt for kartsenter';
    refresh.setAttribute('aria-label', 'Regn på nytt for kartsenter');
    refresh.textContent = '⟳';
    refresh.onclick = () => this.lastForSenter();

    const x = document.createElement('button');
    x.type = 'button';
    x.className = 'report-x';
    x.textContent = '×';
    x.title = 'Lukk';
    x.setAttribute('aria-label', 'Lukk lysforhold');
    x.onclick = () => this.toggle();

    head.append(this.soneBtn, refresh, x);
    p.append(head);

    this.sub = div('lys-sub');
    this.sub.textContent = 'Lysforhold for kartsenter';
    p.append(this.sub);

    this.body = div('lys-body');
    p.append(this.body);
    return p;
  }

  private oppdaterSoneBtn() {
    this.soneBtn.textContent = this.sone === 'UTC' ? 'Z' : 'LOK';
    this.soneBtn.title =
      this.sone === 'UTC'
        ? 'Viser UTC (Zulu). Klikk for lokal tid (Europe/Oslo).'
        : 'Viser lokal tid (Europe/Oslo). Klikk for UTC (Zulu).';
  }

  /** Bygg hele innholdet på nytt fra siste punkt + siste skydekke. */
  private tegn() {
    const punkt = this.sistePunkt;
    if (!punkt) {
      this.body.replaceChildren(tomtilstand('Ingen posisjon valgt — bruk ⟳ for kartsenter.'));
      return;
    }
    const a = lysavlesning(this.nå(), punkt.lat, punkt.lon, this.sisteSky);
    const merke = TIDSSONE_MERKE[this.sone];
    this.sub.textContent =
      `${punkt.lat.toFixed(3)}, ${punkt.lon.toFixed(3)} · ${formaterDatoUtc(a.tidMs)} · `
      + `${formaterKlokke(a.tidMs, this.sone)} ${merke}`;

    const ut: HTMLElement[] = [];
    ut.push(this.nåBlokk(a));
    ut.push(this.bmntEentBlokk(a));
    ut.push(this.tidslinje(a));
    ut.push(this.solBlokk(a));

    const forklaringer: string[] = [];
    const t = tilstandForklaring(a.day, this.sone);
    if (t) forklaringer.push(t);
    forklaringer.push(...terskelForklaringer(a.day));
    if (forklaringer.length) ut.push(merknadsliste('lys-tilstand', forklaringer));

    ut.push(this.måneBlokk(a));
    ut.push(this.nvgBlokk(a));
    ut.push(this.fotnote());
    this.body.replaceChildren(...ut);
  }

  private nåBlokk(a: Lysavlesning): HTMLElement {
    const b = div('lys-na');
    const fase = LYSFASE[a.fase];
    const topp = div('lys-na-topp');
    topp.append(span('lys-na-fase', fase.navn), span('lys-tag', fase.kort));
    b.append(topp);
    b.append(span('lys-na-forklaring', fase.forklaring));
    if (a.nesteOvergang) {
      const n = a.nesteOvergang;
      b.append(
        span(
          'lys-na-neste',
          `Neste overgang: ${LYSFASE[n.til].navn.toLowerCase()} om `
          + `${formaterVarighet(n.sekunder)} (${formaterKlokke(n.atMs, this.sone)} ${TIDSSONE_MERKE[this.sone]})`,
        ),
      );
    } else {
      b.append(
        span(
          'lys-na-neste',
          'Ingen fase-overgang de neste 48 timene — lysforholdene er uendret '
          + '(midnattssol eller mørketid).',
        ),
      );
    }
    return b;
  }

  /** BMNT/EENT er ordre-input — de skal være det første øyet finner. */
  private bmntEentBlokk(a: Lysavlesning): HTMLElement {
    const rad = div('lys-nokkel');
    rad.append(
      this.nøkkelcelle('BMNT', a.day.bmntMs, 'Begin Morning Nautical Twilight (sol −12° stigende)'),
      this.nøkkelcelle('EENT', a.day.eentMs, 'End Evening Nautical Twilight (sol −12° synkende)'),
    );
    return rad;
  }

  private nøkkelcelle(navn: string, atMs: number | null, tittel: string): HTMLElement {
    const c = div('lys-nokkel-celle');
    c.title = tittel;
    c.append(span('lys-nokkel-navn', navn));
    const verdi = span('lys-nokkel-tid', formaterKlokke(atMs, this.sone));
    if (atMs === null) verdi.classList.add('mangler');
    c.append(verdi);
    c.append(
      span(
        'lys-nokkel-sone',
        atMs === null ? 'inntreffer ikke' : `${TIDSSONE_MERKE[this.sone]} · sol −12°`,
      ),
    );
    return c;
  }

  private tidslinje(a: Lysavlesning): HTMLElement {
    const wrap = div('lys-tidslinje');
    wrap.append(seksjonsetikett(
      `Døgnet fra solmidnatt ${formaterKlokke(tidslinjeStartMs(a.day), this.sone)} ${TIDSSONE_MERKE[this.sone]}`,
    ));

    const bar = div('lys-bar');
    for (const b of lysbånd(a.day)) {
      const seg = div(`lys-seg fase-${b.fase}`);
      seg.style.left = `${(b.fra * 100).toFixed(3)}%`;
      seg.style.width = `${((b.til - b.fra) * 100).toFixed(3)}%`;
      seg.title = `${LYSFASE[b.fase].navn}: ${LYSFASE[b.fase].forklaring}`;
      bar.append(seg);
    }
    for (const m of tidsmerker(a.day)) {
      const tick = div(`lys-tick${m.viktig ? ' viktig' : ''}`);
      tick.style.left = `${(m.andel * 100).toFixed(3)}%`;
      tick.title = `${m.navn} ${formaterKlokke(m.atMs, this.sone)} ${TIDSSONE_MERKE[this.sone]}`;
      bar.append(tick);
    }
    const nåAndel = andelIVindu(a.tidMs, a.day);
    if (nåAndel !== null) {
      const nu = div('lys-na-linje');
      nu.style.left = `${(nåAndel * 100).toFixed(3)}%`;
      nu.title = `Nå: ${formaterKlokke(a.tidMs, this.sone)} ${TIDSSONE_MERKE[this.sone]}`;
      bar.append(nu);
    }
    wrap.append(bar);

    const merker = tidsmerker(a.day);
    const etiketter = div('lys-tid-etiketter');
    // Solmiddag har tick i bånden (med tooltip), men ingen etikett: fire etiketter
    // holder seg på ÉN linje i panelbredden, fem wrapper stygt.
    for (const m of merker.filter((x) => x.id !== 'solmiddag')) {
      const e = div(`lys-tid-etikett${m.viktig ? ' viktig' : ''}`);
      e.append(span('lys-te-navn', m.navn), span('lys-te-tid', formaterKlokke(m.atMs, this.sone)));
      etiketter.append(e);
    }
    if (!merker.length) {
      etiketter.append(tomtilstand('Ingen sol-overganger i døgnet — se forklaringen under.'));
    }
    wrap.append(etiketter);
    return wrap;
  }

  private solBlokk(a: Lysavlesning): HTMLElement {
    const b = div('lys-kv');
    const merke = TIDSSONE_MERKE[this.sone];
    b.append(
      kvRad('Soloppgang', a.day.sunriseMs !== null ? `${formaterKlokke(a.day.sunriseMs, this.sone)} ${merke}` : 'inntreffer ikke'),
      kvRad('Solnedgang', a.day.sunsetMs !== null ? `${formaterKlokke(a.day.sunsetMs, this.sone)} ${merke}` : 'inntreffer ikke'),
      kvRad('Borgerlig tussmørke', tussmørkeTekst(a.day.dawnCivilMs, a.day.duskCivilMs, this.sone)),
      kvRad('Astronomisk mørke', tussmørkeTekst(a.day.dawnAstronomicalMs, a.day.duskAstronomicalMs, this.sone)),
      kvRad('Dagslengde', `${a.day.dayLengthHours.toFixed(1)} t`),
      kvRad('Sol nå', `${a.solElevasjonDeg.toFixed(1)}° høyde · asimut ${Math.round(a.solAsimutDeg)}°`),
    );
    return b;
  }

  private måneBlokk(a: Lysavlesning): HTMLElement {
    const b = div('lys-mane');
    b.append(seksjonsetikett('Måne'));
    const topp = div('lys-mane-topp');
    const skive = div('lys-mane-skive');
    // Klarert konstant SVG bygget av egen kode (ingen feltdata) — trygt.
    skive.innerHTML = månefaseSvg(a.månefase.illuminatedFraction, a.månefase.waxing);
    const tekst = div('lys-mane-tekst');
    tekst.append(
      span('lys-mane-navn', a.månefase.phaseName),
      span(
        'lys-mane-tall',
        `${Math.round(a.månefase.illuminatedFraction * 100)} % belyst · `
        + `${a.månefase.ageDays.toFixed(1)} døgn siden nymåne`,
      ),
    );
    topp.append(skive, tekst);
    b.append(topp);
    const kv = div('lys-kv');
    kv.append(
      kvRad('Opp / ned', måneOppNedTekst(a.måne, this.sone)),
      kvRad('Høyde nå', `${a.nvg.moonElevationDeg.toFixed(1)}° · asimut ${Math.round(a.nvg.moonAzimuthDeg)}°`),
    );
    b.append(kv);
    return b;
  }

  private nvgBlokk(a: Lysavlesning): HTMLElement {
    const b = div('lys-nvg');
    b.append(seksjonsetikett('NVG-vurdering (Gen-3, snøfritt terreng)'));
    const nivå = NVG_NIVÅ[a.nvg.level];
    const topp = div('lys-nvg-topp');
    topp.append(span(`lys-nvg-navn niva-${a.nvg.level}`, nivå.navn));
    const trinn = div('lys-nvg-trinn');
    trinn.setAttribute('role', 'img');
    trinn.setAttribute('aria-label', `NVG-nivå ${nivå.trinn} av 5: ${nivå.navn}`);
    for (let i = 1; i <= 5; i++) {
      const t = div(`lys-nvg-boks${i <= nivå.trinn ? ' fylt' : ''}`);
      trinn.append(t);
    }
    topp.append(trinn);
    b.append(topp);
    b.append(span('lys-nvg-forklaring', nivå.forklaring));
    const kv = div('lys-kv');
    kv.append(
      kvRad('Månelys', `${a.nvg.moonlightMilliLux.toFixed(2)} mlx`),
      kvRad('Bakgrunnslys', `${a.nvg.ambientMilliLux.toFixed(2)} mlx`),
      kvRad('Sum', `${a.nvg.totalMilliLux.toFixed(2)} mlx`),
      kvRad(
        'Skydekke',
        a.skydekke.fraksjon === null
          ? 'ikke oppgitt — regnet som klar himmel'
          : `${Math.round(a.skydekke.fraksjon * 100)} % (MET)`,
      ),
    );
    b.append(kv);
    b.append(merknadsliste('lys-forbehold', nvgForbehold(a.nvg, a.skydekke)));
    return b;
  }

  private fotnote(): HTMLElement {
    const f = div('lys-fotnote');
    f.textContent =
      'Astronomi: @skade/terrain (Meeus, verifisert mot USNO). Opp-/nedgang og '
      + 'BMNT/EENT har flere minutters fysisk usikkerhet fra atmosfærisk refraksjon '
      + '— ikke planlegg på sekundet. Terrenghorisont er IKKE med (bruk skygge-laget).';
    return f;
  }
}

// ── Små DOM-hjelpere (samme stil som ui/weather.ts) ──────────────────────────

function tussmørkeTekst(fra: number | null, til: number | null, sone: Tidssone): string {
  if (fra === null && til === null) return 'inntreffer ikke';
  const merke = TIDSSONE_MERKE[sone];
  return `${formaterKlokke(fra, sone)}–${formaterKlokke(til, sone)} ${merke}`;
}

function kvRad(k: string, v: string): HTMLElement {
  const r = div('lys-r');
  r.append(span('lys-rk', k), span('lys-rv', v));
  return r;
}

function seksjonsetikett(t: string): HTMLElement {
  const e = div('lys-seksjon');
  e.textContent = t;
  return e;
}

function merknadsliste(cls: string, linjer: string[]): HTMLElement {
  const ul = document.createElement('ul');
  ul.className = cls;
  for (const l of linjer) {
    const li = document.createElement('li');
    li.textContent = l;
    ul.append(li);
  }
  return ul;
}

/** Tomtilstand med dempet linjeikon + én linje veiledning (UX §8). */
function tomtilstand(tekst: string): HTMLElement {
  const e = div('lys-tom');
  const ikon = span('lys-tom-ikon', '');
  ikon.innerHTML = LYSFORHOLD_IKON;
  e.append(ikon, span('lys-tom-tekst', tekst));
  return e;
}

function div(cls: string): HTMLElement {
  const e = document.createElement('div');
  e.className = cls;
  return e;
}

function span(cls: string, text: string): HTMLElement {
  const e = document.createElement('span');
  e.className = cls;
  e.textContent = text;
  return e;
}
